/**
 * Settings card for "Use my energy for team scheduling". On by default under the Terms of Use;
 * turning it off makes team suggestions and meeting times use only the person's availability.
 *
 * @module apps/web/src/components/settings/TeamEnergySharingCard
 */
import { useEffect, useRef, useState } from 'react';
import { UsersRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { teamSchedulingApi, type TeamEnergySharingState } from '@/lib/teamScheduling';

/** Show and change the caller's team-energy sharing choice. */
export function TeamEnergySharingCard() {
  const [state, setState] = useState<TeamEnergySharingState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Counts choices made on this card; a read that started before the latest choice is stale.
  const changes = useRef(0);

  useEffect(() => {
    let current = true;
    const started = changes.current;
    teamSchedulingApi.getSharing().then(
      value => current && changes.current === started && setState(value),
      cause => current && setError(cause instanceof Error ? cause.message : 'Could not load this setting.')
    );
    return () => {
      current = false;
    };
  }, []);

  /**
   * Show the new choice at once, save it, then show the server's state. A rejected save restores
   * the previous state and shows the error.
   */
  const change = async (enabled: boolean) => {
    const previous = state;
    changes.current += 1;
    if (previous) setState({ ...previous, enabled, source: 'choice' });
    setSaving(true);
    setError('');
    try {
      setState(await teamSchedulingApi.setSharing(enabled));
    } catch (cause) {
      setState(previous);
      setError(cause instanceof Error ? cause.message : 'Could not save this setting.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="team-energy-sharing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UsersRound className="h-5 w-5" aria-hidden="true" />
          Team scheduling
        </CardTitle>
        <CardDescription>
          Teams can suggest who should take on work and find meeting times when people's energy lines up.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <label className="flex items-start gap-3">
          <Checkbox
            checked={state?.enabled ?? true}
            disabled={!state || saving}
            onCheckedChange={checked => change(Boolean(checked))}
            aria-describedby="team-energy-sharing-help"
          />
          <span className="space-y-1">
            <span className="block font-medium">Use my energy for team scheduling</span>
            <span id="team-energy-sharing-help" className="block text-muted-foreground">
              Colleagues never see your energy, check-ins or patterns. They only see a suggested order
              and short reasons such as "lighter week". When this is off, team suggestions and meeting
              times use only your working hours and calendar. On by default under the Terms of Use.
            </span>
          </span>
        </label>
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
